import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { syncHistoryWithStore } from 'react-router-redux';
import { Router, Route, Redirect, hashHistory } from 'react-router';

import store from './store/configureStore';

import {
  userIsAuthenticated,
  userIsNotAuthenticated,
  userHasRoles,
} from './auth';

import Login from './routes/auth/containers/Login';
import SignIn from './routes/auth/containers/SignIn';
import Register from './routes/auth/containers/Register';
import Restore from './routes/auth/containers/Restore';
import Confirmation from './routes/auth/containers/Confirmation';
import Forgot from './routes/auth/containers/Forgot';
import Layout from './components/common/Layout';
import Dashboard from './routes/dashboard/containers/Dashboard';
import UserManagement from './routes/administration/containers/UserManagement';
import UserAuditLog from './routes/administration/containers/UserAuditLog';
import UserEdit from './routes/administration/containers/UserEdit';
import LocationManager from './routes/administration/containers/LocationManager';
import LocationEdit from './routes/administration/containers/LocationEdit';
import LocationManagerDetail from './routes/administration/containers/LocationManagerDetail';
import ClientManager from './routes/administration/containers/ClientManager';
import ClientManagerDetail from './routes/administration/containers/ClientManagerDetail';
import ClientEdit from './routes/administration/containers/ClientEdit';
import EscalationEdit from './routes/administration/containers/EscalationEdit';
import EscalationManager from './routes/administration/containers/EscalationManager';
import ClinicalIncidentReportManager from './routes/administration/containers/ClinicalIncidentReportManager';
import ClinicalIncidentReportEdit from './routes/administration/containers/ClinicalIncidentReportEdit';
import PatientEscalationManager from './routes/administration/containers/PatientEscalationManager';
import LocationEscalationManager from './routes/administration/containers/LocationEscalationManager';
import LocationEscalationEdit from './routes/administration/containers/LocationEscalationEdit';
import EventManager from './routes/administration/containers/EventManager';
import OutreachManager from './routes/administration/containers/OutreachManager';
import OutreachEdit from './routes/administration/containers/OutreachEdit';
import EventEdit from './routes/administration/containers/EventEdit';
import EventManagerDetail from './routes/administration/containers/EventManagerDetail';
import AdvocateDashboard from './routes/management/containers/AdvocateDashboard';
import ClinicActivity from './routes/management/containers/ClinicActivity';
import ChartReview from './routes/management/containers/ChartReview';
import ReviewRequest from './routes/management/containers/ReviewRequest';
import ProtocolFlags from './routes/management/containers/ProtocolFlags';
import ControlledSubs from './routes/management/containers/ControlledSubs';
import FollowUp from './routes/management/containers/FollowUp';
import Alerts from './routes/management/containers/Alerts';
import Patients from './routes/management/containers/Patients';
import PatientProfile from './routes/management/containers/PatientProfile';
import PatientEdit from './routes/management/containers/PatientEdit';
import QueryBuilder from './routes/management/containers/QueryBuilder';
import QueryManager from './routes/management/containers/QueryManager';
import LabDetails from './routes/resource-library/containers/LabDetails';
import CPTLookup from './routes/resource-library/containers/CPTLookup';
import CPTEdit from './routes/resource-library/containers/CPTEdit';
import ICDLookup from './routes/resource-library/containers/ICDLookup';
import ICDEdit from './routes/resource-library/containers/ICDEdit';
import NDCLookup from './routes/resource-library/containers/NDCLookup';
import NDCEdit from './routes/resource-library/containers/NDCEdit';
import ReferralDirectory from './routes/resource-library/containers/ReferralDirectory';
import ReferralEdit from './routes/resource-library/containers/ReferralEdit';
import ReferralDetail from './routes/resource-library/containers/ReferralDetail';
import ReferralCategory from './routes/resource-library/containers/ReferralCategory';
import ReferralCategoryDetail from './routes/resource-library/containers/ReferralCategoryDetail';
import ContactManager from './routes/administration/containers/ContactManager';
import ContactEdit from './routes/administration/containers/ContactEdit';
import AssetManager from './routes/administration/containers/AssetManager';
import AssetEdit from './routes/administration/containers/AssetEdit';
import ClientReport from './routes/reports-analytics/containers/ClientReport';
import LocationReport from './routes/reports-analytics/containers/LocationReport';
import DpcClientReport from './routes/reports-analytics/containers/DpcClientReport';
import DpcMembershipDashboard from './routes/reports-analytics/containers/DpcMembershipDashboard';
import PatientSatisfactionReport from './routes/reports-analytics/containers/PatientSatisfactionReport';
import PMDInternalDailyReport from './routes/reports-analytics/containers/PMDInternalDailyReport';
import PMDInternalWeeklyReport from './routes/reports-analytics/containers/PMDInternalWeeklyReport';
import PMDInternalMonthlyReport from './routes/reports-analytics/containers/PMDInternalMonthlyReport';
import PatientAdvocateDashboard from './routes/reports-analytics/containers/PatientAdvocateDashboard';
import HealthCenterUtilization from './routes/reports-analytics/containers/HealthCenterUtilization';
import HealthCenterDashboard from './routes/reports-analytics/containers/HealthCenterDashboard';
import ReferralDashboard from './routes/reports-analytics/containers/ReferralDashboard';
import COWClientDashboard from './routes/reports-analytics/containers/COWClientDashboard';
import SONMClientDashboard from './routes/reports-analytics/containers/SONMClientDashboard';
import ACClientDashboard from './routes/reports-analytics/containers/ACClientDashboard';
import CONMBClientDashboard from './routes/reports-analytics/containers/CONMBClientDashboard';
import PAIClientReport from './routes/reports-analytics/containers/PAIClientReport';
import PMDDemoClientReport from './routes/reports-analytics/containers/PMDDemoClientReport';
import PGDashboardCOW from './routes/reports-analytics/containers/PGDashboardCOW';
import PGDashboardMCCSC from './routes/reports-analytics/containers/PGDashboardMCCSC';
import PGDashboardLL from './routes/reports-analytics/containers/PGDashboardLL';
import PatientInsuranceReport from './routes/reports-analytics/containers/PatientInsuranceReport';
import ProviderScorecardReport from './routes/reports-analytics/containers/ProviderScorecardReport';
import PatientEligibilityDashboard from './routes/reports-analytics/containers/PatientEligibilityDashboard';
import Files from './routes/reports-analytics/containers/Files';
import VisitNote from './routes/management/containers/VisitNote';
import Profile from './routes/my/containers/Profile';
import TaskManager from './routes/administration/containers/TaskManager';
import TaskEdit from './routes/administration/containers/TaskEdit';
import BillingGroupManager from './routes/administration/containers/BillingGroupManager';
import BillingGroupEdit from './routes/administration/containers/BillingGroupEdit';
import PatientDeductibleStatus from './routes/administration/containers/PatientDeductibleStatus';
import PatientDeductibleStatusDetail from './routes/administration/containers/PatientDeductibleDetail';
import ServiceTypeManager from './routes/service-request-manager/containers/ServiceTypeManager';
import ServiceTypeEdit from './routes/service-request-manager/containers/ServiceTypeEdit';
import ServiceLocationManager from './routes/service-request-manager/containers/ServiceLocationManager';
import ServiceLocationEdit from './routes/service-request-manager/containers/ServiceLocationEdit';
import WorkLocationManager from './routes/service-request-manager/containers/WorkLocationManager';
import WorkLocationEdit from './routes/service-request-manager/containers/WorkLocationEdit';
import ServiceRequestManager from './routes/service-request-manager/containers/ServiceRequestManager';
import ServiceRequestEdit from './routes/service-request-manager/containers/ServiceRequestEdit';
import PatientInsuranceManager from './routes/integrations/containers/PatientInsuranceManager';
import PatientInsuranceEdit from './routes/integrations/containers/PatientInsuranceEdit';

import Page404 from './routes/misc/containers/Page404';

const history = syncHistoryWithStore(hashHistory, store); 

ReactDOM.render(
  <Provider store={store}>
    <Router history={history}>
      <Redirect from="/" to="/mission-control" />
      <Redirect from="/clinic-activity" to="/clinic-activity/all" />
      <Route component={userIsAuthenticated(Layout)}>
        <Route path="mission-control" component={Dashboard} />
        <Route path="profile" component={Profile} />
        {/* Practice Management */}
        <Route path="clinic-activity">
          <Route
            path="all"
            component={userHasRoles(
              ClinicActivity,
              'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isClinicalStaff isExecutive',
            )}
          />
          <Route
            path="chart-review"
            component={userHasRoles(
              ChartReview,
              'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isPhysiciansAssistant isExecutive',
            )}
          />
          <Route
            path="cpt-review-req"
            component={userHasRoles(
              ReviewRequest,
              'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isPhysiciansAssistant isExecutive',
            )}
          />
          <Route
            path="follow-up"
            component={userHasRoles(
              FollowUp,
              'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isClinicalStaff isExecutive',
            )}
          />
          <Route
            path="protocol-flags"
            component={userHasRoles(
              ProtocolFlags,
              'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isClinicalStaff isExecutive',
            )}
          />
          <Route
            path="controlled-subs"
            component={userHasRoles(
              ControlledSubs,
              'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isClinicalStaff isExecutive',
            )}
          />
          <Route
            path="alerts"
            component={userHasRoles(
              Alerts,
              'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isClinicalStaff isExecutive',
            )}
          />
        </Route>
        <Route
          path="patients"
          component={userHasRoles(
            Patients,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isAdmin isClinician isClinicalPharmacist isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isClinicalStaff isManager isStaff isOrganizationalClient isExecutive isMakoAdmin isMakoUser',
          )}
        />
        <Route
          path="patients/patient/:masterPatientId"
          component={userHasRoles(
            PatientEdit,
            'isSuperAdmin isMakoAdmin isMakoUser',
          )}
        />
        <Route
          path="builder"
          component={userHasRoles(QueryBuilder, 'isSuperAdmin')}
        />
        <Route
          path="query-manager"
          component={userHasRoles(QueryManager, 'isSuperAdmin')}
        />
        <Route
          path="outreach-dashboard"
          component={userHasRoles(AdvocateDashboard, 'isSuperAdmin isPatientAdvocate isAdmin isManager isMakoAdmin isMakoUser isClinician isClinicalStaff isSupervisingPhysician isClinicalPharmacist')}
        />
        <Route
          path="patients/profile/:masterPatientId"
          component={userHasRoles(
            PatientProfile,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isAdmin isClinician isClinicalPharmacist isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isClinicalStaff isManager isStaff isOrganizationalClient isExecutive isMakoAdmin isMakoUser',
          )}
        />
        <Route
          path="patients/escalation/:escalationId/:masterPatientId"
          component={userHasRoles(
            EscalationEdit,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isClinicalPharmacist isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive',
          )}
        />
        <Route
          path="visit-note/:id"
          component={userHasRoles(
            VisitNote,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isClinicalStaff isExecutive',
          )}
        />
        {/* Administration */}
        {/* User Management */}
        <Route
          path="user-management"
          component={userHasRoles(UserManagement, 'isSuperAdmin isAdmin')}
        />
        <Route
          path="user-management/user/:id/:mode"
          component={userHasRoles(UserEdit, 'isSuperAdmin isAdmin')}
        />
        <Route
          path="user-audit-log"
          component={userHasRoles(UserAuditLog, 'isSuperAdmin isAdmin isMakoAdmin')}
        />
        {/* Location Management */}
        <Route
          path="location-manager"
          component={userHasRoles(
            LocationManager,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive isMakoAdmin',
          )}
        />
        <Route
          path="location-manager/location/:clinicLocationID"
          component={userHasRoles(
            LocationEdit,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive isMakoAdmin',
          )}
        />
        <Route
          path="location-manager/detail/:clinicLocationID"
          component={userHasRoles(
            LocationManagerDetail,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive isMakoAdmin',
          )}
        />
        {/* Client Management */}
        <Route
          path="client-manager"
          component={userHasRoles(
            ClientManager,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive isMakoAdmin',
          )}
        />
        <Route
          path="client-manager/client/:employerCodeNumber"
          component={userHasRoles(ClientEdit,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive isMakoAdmin')}
        />
        <Route
          path="client-manager/detail/:employerCodeNumber"
          component={userHasRoles(
            ClientManagerDetail,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive isMakoAdmin',
          )}
        />
        {/* Escalation Management */}
        <Route
          path="escalation-manager"
          component={userHasRoles(
            EscalationManager,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isClinicalPharmacist isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive',
          )}
        />
        <Route
          path="patient-escalation"
          component={userHasRoles(
            PatientEscalationManager,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isClinicalPharmacist isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive',
          )}
        />
        <Route
          path="location-escalation"
          component={userHasRoles(
            LocationEscalationManager,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isClinicalPharmacist isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive',
          )}
        />
        <Route
          path="location-escalation/escalation/:escalationId/:locationId"
          component={userHasRoles(
            LocationEscalationEdit,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isClinicalPharmacist isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive',
          )}
        />
        <Route
          path="client-manager/escalation/:escalationId/:employerCodeNumber"
          component={userHasRoles(
            EscalationEdit,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isClinicalPharmacist isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive',
          )}
        />
        <Route
          path="escalation-manager/escalation/:escalationId/:employerCodeNumber"
          component={userHasRoles(
            EscalationEdit,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isClinicalPharmacist isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive',
          )}
        />
        <Route
          path="patient-escalation/escalation/:escalationId/:masterPatientId"
          component={userHasRoles(
            EscalationEdit,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isClinicalPharmacist isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive',
          )}
        />
        {/* Task Management */}
        <Route
          path="task-manager"
          component={userHasRoles(
            TaskManager,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive isMakoAdmin isMakoUser',
          )}
        />
        <Route
          path="task-manager/task/:taskId"
          component={userHasRoles(TaskEdit,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive isMakoAdmin isMakoUser')}
        />
        {/* Billing Group Management */}
        <Route
          path="billing-group-manager"
          component={userHasRoles(
            BillingGroupManager,
            'isSuperAdmin isAdmin isManager isClinicalStaff isExecutive',
          )}
        />
        <Route
          path="billing-group-manager/:billingClientId"
          component={userHasRoles(BillingGroupEdit, 'isSuperAdmin isAdmin isManager isClinicalStaff isExecutive')}
        />
        {/* Patient Deductible Status Management */}
        <Route
          path="patient-deductible-status"
          component={userHasRoles(
            PatientDeductibleStatus,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinicalComplianceManager isClinicalOperationsManager isAdmin isClinician isPatientAdvocate isPhysiciansAssistant isManager isStaff isClinicalStaff isExecutive',
          )}
        />
        <Route
          path="patient-deductible-status/detail/:id"
          component={userHasRoles(
            PatientDeductibleStatusDetail,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinicalComplianceManager isClinicalOperationsManager isAdmin isClinician isPatientAdvocate isPhysiciansAssistant isManager isStaff isClinicalStaff isExecutive',
          )}
        />
        {/* Event Management */}
        <Route
          path="event-manager"
          component={userHasRoles(
            EventManager,
            'isSuperAdmin isAdmin isManager isClinicalStaff isExecutive',
          )}
        />
        <Route
          path="event-manager/event/:meetingId/:employerCodeNumber/:clientName"
          component={userHasRoles(EventEdit, 'isSuperAdmin isAdmin isManager isClinicalStaff isExecutive')}
        />
        <Route
          path="event-manager/detail/:meetingId"
          component={userHasRoles(
            EventManagerDetail,
            'isSuperAdmin isAdmin isManager isClinicalStaff isExecutive',
          )}
        />
        <Route
          path="cir-manager"
          component={userHasRoles(
            ClinicalIncidentReportManager,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isClinicalPharmacist isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive',
          )}
        />
        <Route
        path="cir-manager/report/:id/:mode"
        component={userHasRoles(
          ClinicalIncidentReportEdit,
          'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isClinician isClinicalPharmacist isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isPhysiciansAssistant isAdmin isManager isClinicalStaff isStaff isExecutive',
        )}
        />
        <Route
          path="contact-manager"
          component={userHasRoles(
            ContactManager,
            'isSuperAdmin isAdmin isManager isClinicalStaff isExecutive',
          )}
        />
        <Route path="contact-manager/contact/:id/:mode" component={ContactEdit} />
        <Route path="client-manager/contact/:id/:mode/:employerCodeNumber" component={ContactEdit} />
        <Route path="location-manager/contact/:id/:mode/:clinicLocationID" component={ContactEdit} />
        <Route path="patients/contact/:id/:mode/:masterPatientId" component={ContactEdit} />
        <Route
          path="asset-manager"
          component={userHasRoles(
            AssetManager,
            'isSuperAdmin isAdmin isManager isClinicalStaff isExecutive',
          )}
        />
        <Route path="asset-manager/asset/:id/:mode" component={AssetEdit} />
        <Route
          path="outreach-manager"
          component={userHasRoles(
            OutreachManager,
            'isSuperAdmin isAdmin isManager isClinicalStaff isExecutive',
          )}
        />
        <Route path="outreach-manager/outreach/:id" component={OutreachEdit} />
        {/* Reports & Analytics */}
        <Route
          path="client-report(/:client)"
          component={userHasRoles(
            ClientReport,
            'isSuperAdmin isExternalStakeholder isOrganizationalClient',
          )}
        />
        <Route
          path="location-report(/:location)"
          component={userHasRoles(
            LocationReport,
            'isSuperAdmin isAdministrativeProvider isSupervisingPhysician isManager isPatientAdvocate isClinicalComplianceManager isClinicalOperationsManager isAdmin isStaff isExternalStakeholder isClinicalStaff isExecutive',
          )}
        />
        <Route
          path="dpc-report"
          component={userHasRoles(
            DpcClientReport,
            'isSuperAdmin',
          )}
        />
        <Route
          path="dpc-membership-dashboard"
          component={userHasRoles(
            DpcMembershipDashboard,
            'isSuperAdmin',
          )}
        />
        <Route
          path="patient-satisfaction-report"
          component={userHasRoles(
            PatientSatisfactionReport,
            'isSuperAdmin isAdmin isManager isClinicalOperationsManager',
          )}
        />
        <Route
          path="pmd-internal-daily-report"
          component={userHasRoles(
            PMDInternalDailyReport,
            'isSuperAdmin',
          )}
        />
        <Route
          path="pmd-internal-weekly-report"
          component={userHasRoles(
            PMDInternalWeeklyReport,
            'isSuperAdmin',
          )}
        />
        <Route
          path="pmd-internal-monthly-report"
          component={userHasRoles(
            PMDInternalMonthlyReport,
            'isSuperAdmin',
          )}
        />
        <Route
          path="patient-advocate-dashboard"
          component={userHasRoles(
            PatientAdvocateDashboard,
            'isSuperAdmin',
          )}
        />
        <Route
          path="healthcenter-utilization"
          component={userHasRoles(
            HealthCenterUtilization,
            'isSuperAdmin',
          )}
        />
        <Route
          path="health-center-dashboard"
          component={userHasRoles(
            HealthCenterDashboard,
            'isSuperAdmin isClinicalOperationsManager isClinicalComplianceManager isManager isAdmin isClinician isSupervisingPhysician isClinicalStaff isPatientAdvocate',
          )}
        />
        <Route
          path="referral-dashboard"
          component={userHasRoles(
            ReferralDashboard,
            'isSuperAdmin isAdmin isManager isPatientAdvocate isClinicalOperationsManager isClinicalComplianceManager isClinician isClinicalStaff',
          )}
        />
        <Route
          path="cow-client-dashboard(/:client)"
          component={userHasRoles(
            COWClientDashboard,
            'isSuperAdmin isAdmin isExternalStakeholder',
          )}
        />
        <Route
          path="sonm-client-dashboard(/:client)"
          component={userHasRoles(
            SONMClientDashboard,
            'isSuperAdmin isAdmin isExternalStakeholder',
          )}
        />
        <Route
          path="ac-client-dashboard(/:client)"
          component={userHasRoles(
            ACClientDashboard,
            'isSuperAdmin isAdmin isExternalStakeholder',
          )}
        />
        <Route
          path="conmb-client-dashboard(/:client)"
          component={userHasRoles(
            CONMBClientDashboard,
            'isSuperAdmin isAdmin isExternalStakeholder',
          )}
        />
        <Route
          path="pai-client-report(/:client)"
          component={userHasRoles(
            PAIClientReport,
            'isSuperAdmin isAdmin isExternalStakeholder',
          )}
        />
        <Route
          path="pmd-demo-client-report(/:client)"
          component={userHasRoles(
            PMDDemoClientReport,
            'isSuperAdmin isAdmin isExternalStakeholder',
          )}
        />
        <Route
          path="pg-dashboard-cow"
          component={userHasRoles(
            PGDashboardCOW,
            'isSuperAdmin isAdmin isPatientAdvocate isClinicalOperationsManager isManager isClinician isClinicalStaff isSupervisingPhysician isAdministrativeProvider',
          )}
        />
        <Route
          path="pg-dashboard-mccsc"
          component={userHasRoles(
            PGDashboardMCCSC,
            'isSuperAdmin isAdmin isPatientAdvocate isClinicalOperationsManager isManager isClinician isClinicalStaff isSupervisingPhysician isAdministrativeProvider',
          )}
        />
        <Route
          path="pg-dashboard-lnl"
          component={userHasRoles(
            PGDashboardLL,
            'isSuperAdmin isAdmin isPatientAdvocate isClinicalOperationsManager isManager isClinician isClinicalStaff isSupervisingPhysician isAdministrativeProvider',
          )}
        />
        <Route
          path="patient-insurance-report"
          component={userHasRoles(
            PatientInsuranceReport,
            'isSuperAdmin isAdmin isPatientAdvocate isClinicalOperationsManager isManager isClinician isClinicalPharmacist isClinicalStaff isSupervisingPhysician isAdministrativeProvider',
          )}
        />
        <Route
          path="provider-scorecard"
          component={userHasRoles(
            ProviderScorecardReport,
            'isSuperAdmin isClinicalOperationsManager isManager isAdmin isClinician isSupervisingPhysician',
          )}
        />
        <Route
          path="patient-eligibility-report"
          component={userHasRoles(
            PatientEligibilityDashboard,
            'isSuperAdmin isAdmin isPatientAdvocate isClinicalOperationsManager isClinicalComplianceManager isManager isClinicalStaff isClinician isClinicalPharmacist',
          )}
        />
        <Route
          path="files(/:client)"
          component={userHasRoles(
            Files,
            'isSuperAdmin isExternalStakeholder isOrganizationalClient isExecutive',
          )}
        />
        {/* Resource Library */}
        <Route path="lab-details" component={LabDetails} />
        <Route path="cpt-lookup" component={CPTLookup} />
        <Route path="cpt-lookup/cpt/:procedure" component={CPTEdit} />
        <Route path="icd-lookup" component={ICDLookup} />
        <Route path="icd-lookup/icd/:key" component={ICDEdit} />
        <Route path="ndc-lookup" component={NDCLookup} />
        <Route path="ndc-lookup/ndc/:productId" component={NDCEdit} />
        <Route path="referral-directory" component={ReferralDirectory} />
        <Route path="referral-directory/referral/:id/:mode" component={ReferralEdit} />
        <Route path="referral-directory/detail/:id" component={ReferralDetail} />
        <Route path="referral-category" component={ReferralCategory} />
        <Route path="referral-category/detail/:id/:category" component={ReferralCategoryDetail} />
        {/* Service Request Management */}
        <Route
          path="service-manager"
          component={userHasRoles(
            ServiceTypeManager,
            'isSuperAdmin isCareNavigator',
          )}
        />
        <Route
          path="service-manager/service/:id"
          component={userHasRoles(ServiceTypeEdit, 'isSuperAdmin isCareNavigator')}
        />
        <Route
          path="service-loc-manager"
          component={userHasRoles(
            ServiceLocationManager,
            'isSuperAdmin isCareNavigator',
          )}
        />
        <Route
          path="service-loc-manager/location/:id"
          component={userHasRoles(ServiceLocationEdit, 'isSuperAdmin isCareNavigator')}
        />
        <Route
          path="work-loc-manager"
          component={userHasRoles(
            WorkLocationManager,
            'isSuperAdmin isCareNavigator',
          )}
        />
        <Route
          path="work-loc-manager/location/:id"
          component={userHasRoles(WorkLocationEdit, 'isSuperAdmin isCareNavigator')}
        />
        <Route
          path="service-request-worklist"
          component={userHasRoles(
            ServiceRequestManager,
            'isSuperAdmin isCareNavigator isExternalStakeholder',
          )}
        />
        <Route
          path="service-request-worklist/request/:id/:mode"
          component={userHasRoles(ServiceRequestEdit, 'isSuperAdmin isCareNavigator isExternalStakeholder')}
        />
        <Route
          path="athena-patient-insurance"
          component={userHasRoles(
            PatientInsuranceManager,
            'isSuperAdmin',
          )}
        />
        <Route
          path="athena-patient-insurance/insurance/:id"
          component={userHasRoles(PatientInsuranceEdit, 'isSuperAdmin')}
        />
      </Route>
      <Route path="login" component={userIsNotAuthenticated(Login)} />
      <Route path="signin" component={userIsNotAuthenticated(SignIn)} />
      <Route path="register" component={userIsNotAuthenticated(Register)} />
      <Route path="forgot" component={userIsNotAuthenticated(Forgot)} />
      <Route path="restore" component={Restore} />
      <Route path="confirmation" component={Confirmation} />
      <Route path="*" component={Page404} />
    </Router>
  </Provider>,
  document.getElementById('root'),
);



// WEBPACK FOOTER //
// ./src/app.js